/**
 * Google Drive API playground - harness mode for ?test=google-drive-extension
 */

import { useCallback, useState } from 'react'
import {
  AlertCircle,
  Download,
  FlaskConical,
  Loader2,
  MessageSquare,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useGoogleDriveAuth } from '@/hooks/use-google-drive-auth'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

export async function openOAuthPopup(): Promise<void> {
  const res = await fetchWithAuth('/api/google-drive/auth/url');
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body?.error === 'string' ? body.error : 'Failed to get OAuth URL');
  }
  const data = (await res.json()) as { url: string };
  window.open(data.url, '_blank', 'popup,width=500,height=650,noopener,noreferrer');
}

export function ApiTestPanel() {
  const { authStatus, isLoading: authLoading, error: authError, refetch } = useGoogleDriveAuth();
  const connected = authStatus?.connected ?? false;

  // Import by file ID
  const [importFileId, setImportFileId] = useState('');
  const [importTitle, setImportTitle] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // List comments
  const [commentsFileId, setCommentsFileId] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsResult, setCommentsResult] = useState<string | null>(null);
  const [commentsError, setCommentsError] = useState<string | null>(null);

  // Push
  const [pushDocId, setPushDocId] = useState('');
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  const handleConnect = useCallback(async () => {
    try {
      await openOAuthPopup();
    } catch (err) {
      console.error('OAuth popup failed:', err);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    const res = await fetchWithAuth('/api/google-drive/auth/disconnect', { method: 'DELETE' });
    if (res.ok) {
      await refetch();
    }
  }, [refetch]);

  const handleImport = useCallback(async () => {
    if (!importFileId.trim()) return;
    setImportLoading(true);
    setImportResult(null);
    setImportError(null);
    try {
      const res = await fetchWithAuth('/api/google-drive/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId: importFileId.trim(),
          ...(importTitle.trim() && { title: importTitle.trim() }),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${res.status}`);
      }
      setImportResult(JSON.stringify(data, null, 2));
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImportLoading(false);
    }
  }, [importFileId, importTitle]);

  const handleListComments = useCallback(async () => {
    if (!commentsFileId.trim()) return;
    setCommentsLoading(true);
    setCommentsResult(null);
    setCommentsError(null);
    try {
      const res = await fetchWithAuth(`/api/google-drive/files/${encodeURIComponent(commentsFileId.trim())}/comments`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${res.status}`);
      }
      setCommentsResult(JSON.stringify(data, null, 2));
    } catch (err) {
      setCommentsError(err instanceof Error ? err.message : 'Failed to list comments');
    } finally {
      setCommentsLoading(false);
    }
  }, [commentsFileId]);

  const handlePush = useCallback(async () => {
    if (!pushDocId.trim()) return;
    setPushLoading(true);
    setPushResult(null);
    setPushError(null);
    try {
      const res = await fetchWithAuth('/api/google-drive/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: pushDocId.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${res.status}`);
      }
      setPushResult(JSON.stringify(data, null, 2));
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Push failed');
    } finally {
      setPushLoading(false);
    }
  }, [pushDocId]);

  return (
    <div className="flex-1 overflow-auto p-6 space-y-6 bg-background">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-primary" />
            Google Drive API Playground
          </h1>
          <p className="text-sm text-muted-foreground">Test the live API endpoints. All calls use <code className="text-xs bg-muted px-1 rounded">fetchWithAuth</code> with your Supabase session JWT.</p>
        </div>

        {/* Auth Status */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h2 className="text-sm font-medium">Auth Status</h2>
          <div
            className="flex items-center gap-3 text-sm"
            data-testid={testIds.googleDriveTest.authStatus}
          >
            {authLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', connected ? 'bg-green-500' : 'bg-muted-foreground/50')} />
            )}
            <span>
              {authLoading ? 'Checking...' : connected ? 'Connected to Google Drive' : 'Not connected'}
            </span>
            <button
              onClick={() => void refetch()}
              className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
              title="Refresh auth status"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          {authError && (
            <p className="text-xs text-destructive">{authError}</p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={connected || authLoading}
              className="gap-1.5"
              data-testid={testIds.googleDriveTest.connectButton}
            >
              <span className="text-sm leading-none">G</span>
              Connect
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDisconnect}
              disabled={!connected || authLoading}
              className="gap-1.5 text-destructive hover:text-destructive"
              data-testid={testIds.googleDriveTest.disconnectButton}
            >
              <X className="w-3.5 h-3.5" />
              Disconnect
            </Button>
          </div>
        </div>

        {/* Import by File ID */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="space-y-0.5">
            <h2 className="text-sm font-medium">Import Document</h2>
            <p className="text-xs text-muted-foreground"><code className="bg-muted px-1 rounded">POST /api/google-drive/import</code></p>
          </div>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Google Drive File ID *</label>
              <Input
                placeholder="1BxiMVs0XRA5nFMdKvBgBPT..."
                value={importFileId}
                onChange={e => setImportFileId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && void handleImport()}
                className="text-sm font-mono"
                data-testid={testIds.googleDriveTest.importFileIdInput}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Title (optional)</label>
              <Input
                placeholder="My imported doc"
                value={importTitle}
                onChange={e => setImportTitle(e.target.value)}
                className="text-sm"
                data-testid={testIds.googleDriveTest.importTitleInput}
              />
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={!importFileId.trim() || importLoading || !connected}
            className="gap-1.5"
            data-testid={testIds.googleDriveTest.importSubmit}
          >
            {importLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Import
          </Button>
          {importError && (
            <p className="text-xs text-destructive flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              {importError}
            </p>
          )}
          {importResult && (
            <pre
              className="text-xs bg-muted rounded p-3 overflow-auto max-h-48 text-green-600 dark:text-green-400"
              data-testid={testIds.googleDriveTest.importResult}
            >
              {importResult}
            </pre>
          )}
        </div>

        {/* List Comments */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="space-y-0.5">
            <h2 className="text-sm font-medium">List Comments</h2>
            <p className="text-xs text-muted-foreground"><code className="bg-muted px-1 rounded">GET /api/google-drive/files/:fileId/comments</code></p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Google Drive File ID *</label>
            <Input
              placeholder="1BxiMVs0XRA5nFMdKvBgBPT..."
              value={commentsFileId}
              onChange={e => setCommentsFileId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && void handleListComments()}
              className="text-sm font-mono"
              data-testid={testIds.googleDriveTest.commentsFileIdInput}
            />
          </div>
          <Button
            size="sm"
            onClick={handleListComments}
            disabled={!commentsFileId.trim() || commentsLoading || !connected}
            className="gap-1.5"
            data-testid={testIds.googleDriveTest.commentsSubmit}
          >
            {commentsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
            Fetch Comments
          </Button>
          {commentsError && (
            <p className="text-xs text-destructive flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              {commentsError}
            </p>
          )}
          {commentsResult && (
            <pre
              className="text-xs bg-muted rounded p-3 overflow-auto max-h-64 text-green-600 dark:text-green-400"
              data-testid={testIds.googleDriveTest.commentsResult}
            >
              {commentsResult}
            </pre>
          )}
        </div>

        {/* Push to Drive */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="space-y-0.5">
            <h2 className="text-sm font-medium">Push to Drive</h2>
            <p className="text-xs text-muted-foreground"><code className="bg-muted px-1 rounded">POST /api/google-drive/push</code></p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">FlowNotes Document UUID *</label>
            <Input
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={pushDocId}
              onChange={e => setPushDocId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && void handlePush()}
              className="text-sm font-mono"
              data-testid={testIds.googleDriveTest.pushDocIdInput}
            />
          </div>
          <Button
            size="sm"
            onClick={handlePush}
            disabled={!pushDocId.trim() || pushLoading || !connected}
            className="gap-1.5"
            data-testid={testIds.googleDriveTest.pushSubmit}
          >
            {pushLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Push to Drive
          </Button>
          {pushError && (
            <p className="text-xs text-destructive flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              {pushError}
            </p>
          )}
          {pushResult && (
            <pre
              className="text-xs bg-muted rounded p-3 overflow-auto max-h-48 text-green-600 dark:text-green-400"
              data-testid={testIds.googleDriveTest.pushResult}
            >
              {pushResult}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

